import React from 'react';
import { Helmet } from 'react-helmet-async';

const getOrigin = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  // Fallback for SSR - will be replaced with actual domain on client-side
  return typeof window !== 'undefined' ? window.location.origin : '';
};

export const HomeSEO = () => {
  const currentOrigin = getOrigin();
  
  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "PhysicalTherapy", "LocalBusiness"],
    "@id": currentOrigin || "https://www.gymratrehab.com",
    "name": "Gym Rat Rehab LLC",
    "alternateName": "GymRatRehab",
    "description": "Expert cash-based physical therapy specializing in sports injuries, orthopedic rehabilitation, manual therapy, and vertigo care in Gilbert, Arizona.",
    "url": currentOrigin || "https://www.gymratrehab.com",
    "telephone": "+14807579713",
    "email": "Info@gymratrehab.com",
    "priceRange": "$$",
    "image": "https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2680 S Val Vista Dr Ste.131, Bldg. 6",
      "addressLocality": "Gilbert",
      "addressRegion": "AZ",
      "postalCode": "85295",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "33.2843",
      "longitude": "-111.7490"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Tuesday", "Wednesday", "Thursday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "areaServed": [
      {
        "@type": "City",
        "name": "Gilbert",
        "containedIn": {
          "@type": "State",
          "name": "Arizona"
        }
      },
      {
        "@type": "City",
        "name": "Chandler"
      },
      {
        "@type": "City",
        "name": "Mesa"
      },
      {
        "@type": "City",
        "name": "Queen Creek"
      },
      {
        "@type": "City",
        "name": "San Tan Valley"
      },
      {
        "@type": "City",
        "name": "Apache Junction"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Physical Therapy Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalTherapy",
            "name": "Sports Injury Recovery",
            "description": "Specialized treatment for sports-related injuries including ACL/MCL tears, sprains, and athletic performance optimization"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalTherapy",
            "name": "Orthopedic Rehabilitation",
            "description": "Comprehensive rehabilitation for orthopedic conditions, post-surgical recovery, and musculoskeletal disorders"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalTherapy",
            "name": "Manual Therapy",
            "description": "Advanced manual techniques including dry needling, cupping, myofascial release, and joint mobilization"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "MedicalTherapy",
            "name": "Vertigo & Dizziness Care",
            "description": "Vestibular rehabilitation for BPPV, dizziness, balance disorders, and post-concussion symptoms"
          }
        }
      ]
    },
    "founder": {
      "@type": "Person",
      "name": "Douglas Mitchell",
      "jobTitle": "Physical Therapist, DPT",
      "description": "Doctor of Physical Therapy with 20+ years experience specializing in sports injuries and orthopedic rehabilitation"
    },
    "paymentAccepted": "Cash, Credit Card, Debit Card",
    "currenciesAccepted": "USD",
    "keywords": "physical therapy gilbert az, cash based physical therapy, sports injury recovery, orthopedic rehabilitation, manual therapy, dry needling, vertigo treatment, vestibular therapy, physical therapist gilbert"
  };

  return (
    <Helmet>
      <title>Physical Therapy Gilbert AZ | Cash-Based PT | Gym Rat Rehab LLC</title>
      <meta name="description" content="Expert cash-based physical therapy in Gilbert, AZ. Specializing in sports injuries, orthopedic rehab, manual therapy & vertigo care. Douglas Mitchell, PT, DPT - 20+ years experience. Call (480) 757-9713" />
      <meta name="keywords" content="physical therapy gilbert az, cash based physical therapy arizona, sports injury recovery phoenix, orthopedic rehabilitation gilbert, manual therapy arizona, dry needling gilbert, vertigo treatment gilbert az, physical therapist gilbert" />
      
      {/* Geo-targeting */}
      <meta name="geo.region" content="US-AZ" />
      <meta name="geo.placename" content="Gilbert" />
      <meta name="geo.position" content="33.2843;-111.7490" />
      <meta name="ICBM" content="33.2843, -111.7490" />
      
      <link rel="canonical" href={`${currentOrigin}/`} />
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Physical Therapy Gilbert AZ | Cash-Based PT | Gym Rat Rehab LLC" />
      <meta property="og:description" content="Expert cash-based physical therapy in Gilbert, AZ. Sports injuries, orthopedic rehab, manual therapy & vertigo care. 20+ years experience. Call (480) 757-9713" />
      <meta property="og:url" content={`${currentOrigin}/`} />
      <meta property="og:site_name" content="Gym Rat Rehab LLC" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Gym Rat Rehab LLC - Physical Therapy in Gilbert, Arizona" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Physical Therapy Gilbert AZ | Cash-Based PT | Gym Rat Rehab LLC" />
      <meta name="twitter:description" content="Expert cash-based physical therapy in Gilbert, AZ. Sports injuries, orthopedic rehab, manual therapy & vertigo care. 20+ years experience." />
      <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_fitness-recovery/artifacts/a7sdec6g_gym%20rat%20banner%20revised.jpg" />
      
      {/* Structured Data - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};